import type { ConnectorId, ConnectorStatus } from './ConnectorTypes'

/**
 * Lightweight event bus for connector lifecycle.
 * Prepare for UI / telemetry subscribers without coupling to app modules.
 */

export type ConnectorEventType =
  | 'status_changed'
  | 'connect_start'
  | 'connect_success'
  | 'connect_error'
  | 'disconnect'
  | 'session_refresh'
  | 'search'
  | 'health'
  | 'error'

export interface ConnectorEventMap {
  status_changed: {
    connectorId: ConnectorId
    from: ConnectorStatus
    to: ConnectorStatus
  }
  connect_start: { connectorId: ConnectorId }
  connect_success: { connectorId: ConnectorId }
  connect_error: { connectorId: ConnectorId; error: string }
  disconnect: { connectorId: ConnectorId }
  session_refresh: { connectorId: ConnectorId; sessionId?: string }
  search: { connectorId: ConnectorId; query: string; resultCount: number }
  health: { connectorId: ConnectorId; healthy: boolean }
  error: { connectorId: ConnectorId; message: string; code?: string }
}

export type ConnectorEventHandler<T extends ConnectorEventType> = (
  payload: ConnectorEventMap[T],
) => void

export class ConnectorEventBus {
  private readonly listeners = new Map<ConnectorEventType, Set<ConnectorEventHandler<ConnectorEventType>>>()

  on<T extends ConnectorEventType>(type: T, handler: ConnectorEventHandler<T>): () => void {
    const set = this.listeners.get(type) ?? new Set()
    set.add(handler as ConnectorEventHandler<ConnectorEventType>)
    this.listeners.set(type, set)
    return () => {
      set.delete(handler as ConnectorEventHandler<ConnectorEventType>)
    }
  }

  emit<T extends ConnectorEventType>(type: T, payload: ConnectorEventMap[T]): void {
    const set = this.listeners.get(type)
    if (!set) return
    for (const handler of set) {
      try {
        handler(payload)
      } catch {
        // Subscriber failures must not break connectors.
      }
    }
  }

  clear(): void {
    this.listeners.clear()
  }
}

export const connectorEvents = new ConnectorEventBus()
